import type { UserDetail } from "~/types/User";
import type { UserSpace } from "~/constants/cookies";

export const useUserStore = defineStore(
  "user",
  () => {
    const user = ref<UserDetail | null>();
    const selectedSpace = ref<UserSpace>(null);

    async function loadUser() {
      const identity = useSanctumUser<UserDetail & { roles_and_permissions?: unknown }>()?.value;
      // This store is persisted in a cookie (4 kB limit, sent on every request):
      // keep the ~2.5 kB of rights out of it. The access guard reads them from
      // the sanctum identity.
      if (identity) {
        const { roles_and_permissions: _rolesAndPermissions, ...userWithoutRights } = identity;
        user.value = userWithoutRights;
      }
      else {
        user.value = identity;
      }
    }

    async function getUser() {
      if (!user.value) {
        await loadUser();
      }
      return readonly(user);
    }

    function clearUser() {
      user.value = null;
      selectedSpace.value = null;
    }

    function setSelectedSpace(space: UserSpace) {
      selectedSpace.value = space;
    }

    function getSelectedSpace() {
      return selectedSpace.value;
    }

    function clearSelectedSpace() {
      selectedSpace.value = null;
    }

    return {
      user: readonly(user),
      loadUser,
      getUser,
      clearUser,
      selectedSpace: readonly(selectedSpace),
      setSelectedSpace,
      getSelectedSpace,
      clearSelectedSpace,
    };
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.cookies(),
    },
  },
);
